import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Form, Modal, Select, message, Tag, Row, Col, InputNumber, Card, Statistic, DatePicker, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, DollarOutlined, EyeOutlined, WarningOutlined } from '@ant-design/icons';
import { payableAPI, supplierAPI } from '../../services/api';
import dayjs from 'dayjs';

const { Search } = Input;
const { Option } = Select;
const { TextArea } = Input;

const PayableList = () => {
  const [payables, setPayables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({ status: '', supplierId: '' });
  const [modalVisible, setModalVisible] = useState(false);
  const [payModalVisible, setPayModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingPayable, setEditingPayable] = useState(null);
  const [currentPayable, setCurrentPayable] = useState(null);
  const [payableDetail, setPayableDetail] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [stats, setStats] = useState({});
  const [form] = Form.useForm();
  const [payForm] = Form.useForm();

  const statusMap = {
    'pending': { text: '待付款', color: 'orange' },
    'partial': { text: '部分付款', color: 'blue' },
    'completed': { text: '已完成', color: 'green' },
    'overdue': { text: '已逾期', color: 'red' },
    'cancelled': { text: '已取消', color: 'default' }
  };

  const paymentMethodMap = {
    'cash': '现金',
    'bank': '银行转账',
    'wechat': '微信',
    'alipay': '支付宝',
    'check': '支票',
    'other': '其他'
  };

  const columns = [
    {
      title: '应付单号',
      dataIndex: 'billNo',
      key: 'billNo',
      width: 140
    },
    {
      title: '供应商',
      dataIndex: ['supplier', 'name'],
      key: 'supplierName',
      width: 150,
      render: (text, record) => record.supplier?.shortName || text
    },
    {
      title: '关联单号',
      dataIndex: 'relatedOrderNo',
      key: 'relatedOrderNo',
      width: 140,
      render: (text) => text || '-'
    },
    {
      title: '应付金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (text) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>¥{parseFloat(text).toFixed(2)}</span>
    },
    {
      title: '已付金额',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      width: 120,
      render: (text) => <span style={{ color: '#52c41a' }}>¥{parseFloat(text).toFixed(2)}</span>
    },
    {
      title: '待付金额',
      dataIndex: 'pendingAmount',
      key: 'pendingAmount',
      width: 120,
      render: (text) => {
        const amount = parseFloat(text);
        return <span style={{ color: amount > 0 ? '#ff4d4f' : '#52c41a' }}>¥{amount.toFixed(2)}</span>;
      }
    },
    {
      title: '账单日期',
      dataIndex: 'billDate',
      key: 'billDate',
      width: 110,
      render: (text) => text ? dayjs(text).format('YYYY-MM-DD') : '-'
    },
    {
      title: '到期日期',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 110,
      render: (text, record) => {
        if (!text) return '-';
        const isOverdue = record.status !== 'completed' && dayjs(text).isBefore(dayjs(), 'day');
        return (
          <span style={{ color: isOverdue ? '#ff4d4f' : undefined }}>
            {dayjs(text).format('YYYY-MM-DD')}
            {isOverdue && <WarningOutlined style={{ marginLeft: 4 }} />}
          </span>
        );
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text) => {
        const status = statusMap[text] || { text, color: 'default' };
        return <Tag color={status.color}>{status.text}</Tag>;
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)}>
            详情
          </Button>
          {record.status !== 'completed' && record.status !== 'cancelled' && (
            <Button type="link" icon={<DollarOutlined />} onClick={() => handlePay(record)}>
              付款
            </Button>
          )}
          {record.status === 'pending' && parseFloat(record.paidAmount) === 0 && (
            <>
              <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                编辑
              </Button>
              <Popconfirm
                title="确定要删除这条应付记录吗?"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="link" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchSuppliers();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchPayables();
  }, [pagination.current, pagination.pageSize, searchText, filters]);

  const fetchSuppliers = async () => {
    try {
      const response = await supplierAPI.getList({ limit: 1000 });
      setSuppliers(response.data || []);
    } catch (error) {
      console.error('获取供应商列表失败', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await payableAPI.getStats();
      setStats(response);
    } catch (error) {
      console.error('获取统计失败', error);
    }
  };

  const fetchPayables = async () => {
    try {
      setLoading(true);
      const response = await payableAPI.getList({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        ...filters
      });

      setPayables(response.data);
      setPagination(prev => ({
        ...prev,
        total: response.pagination.total
      }));
    } catch (error) {
      message.error('获取应付账款列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleReset = () => {
    setSearchText('');
    setFilters({ status: '', supplierId: '' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleAdd = () => {
    setEditingPayable(null);
    form.resetFields();
    form.setFieldsValue({
      billDate: dayjs(),
      status: 'pending'
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingPayable(record);
    form.setFieldsValue({
      ...record,
      billDate: record.billDate ? dayjs(record.billDate) : null,
      dueDate: record.dueDate ? dayjs(record.dueDate) : null
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        billDate: values.billDate?.format('YYYY-MM-DD'),
        dueDate: values.dueDate?.format('YYYY-MM-DD')
      };

      if (editingPayable) {
        await payableAPI.update(editingPayable.id, data);
        message.success('更新成功');
      } else {
        await payableAPI.create(data);
        message.success('创建成功');
      }

      setModalVisible(false);
      fetchPayables();
      fetchStats();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '操作失败');
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await payableAPI.delete(id);
      message.success('删除成功');
      fetchPayables();
      fetchStats();
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handlePay = (record) => {
    setCurrentPayable(record);
    payForm.resetFields();
    payForm.setFieldsValue({
      amount: parseFloat(record.pendingAmount),
      paymentMethod: 'bank',
      paymentDate: dayjs()
    });
    setPayModalVisible(true);
  };

  const handlePayOk = async () => {
    try {
      const values = await payForm.validateFields();
      const data = {
        ...values,
        paymentDate: values.paymentDate?.format('YYYY-MM-DD')
      };

      await payableAPI.pay(currentPayable.id, data);
      message.success('付款成功');
      setPayModalVisible(false);
      fetchPayables();
      fetchStats();
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写完整的表单');
      } else {
        message.error(error.response?.data?.message || '付款失败');
      }
    }
  };

  const handleViewDetail = async (record) => {
    try {
      const response = await payableAPI.getDetail(record.id);
      setPayableDetail(response);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>应付账款管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          新增应付
        </Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card>
            <Statistic title="应付总额" value={stats.totalAmount || 0} prefix="¥" precision={2} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="已付金额" value={stats.paidAmount || 0} prefix="¥" precision={2} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="待付金额" value={stats.pendingAmount || 0} prefix="¥" precision={2} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="待付款单" value={stats.pendingCount || 0} suffix="笔" />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="部分付款" value={stats.partialCount || 0} suffix="笔" valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic title="逾期金额" value={stats.overdueAmount || 0} prefix="¥" precision={2} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Search
              placeholder="搜索单号"
              onSearch={handleSearch}
              style={{ width: 200 }}
              allowClear
            />
          </Col>
          <Col>
            <Select
              placeholder="选择供应商"
              style={{ width: 150 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              value={filters.supplierId || undefined}
              onChange={(value) => handleFilterChange('supplierId', value)}
            >
              {suppliers.map(s => (
                <Option key={s.id} value={s.id}>{s.shortName || s.name}</Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select
              placeholder="状态"
              style={{ width: 120 }}
              allowClear
              value={filters.status || undefined}
              onChange={(value) => handleFilterChange('status', value)}
            >
              <Option value="pending">待付款</Option>
              <Option value="partial">部分付款</Option>
              <Option value="completed">已完成</Option>
              <Option value="overdue">已逾期</Option>
            </Select>
          </Col>
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              重置
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={payables}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条记录`
        }}
        onChange={handleTableChange}
        scroll={{ x: 1400 }}
      />

      {/* 新增/编辑弹窗 */}
      <Modal
        title={editingPayable ? '编辑应付单' : '新增应付单'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="supplierId"
                label="供应商"
                rules={[{ required: true, message: '请选择供应商' }]}
              >
                <Select
                  placeholder="请选择供应商"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {suppliers.map(s => (
                    <Option key={s.id} value={s.id}>{s.shortName || s.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="totalAmount"
                label="应付金额"
                rules={[{ required: true, message: '请输入应付金额' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0.01}
                  precision={2}
                  prefix="¥"
                  placeholder="请输入应付金额"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="billDate"
                label="账单日期"
                rules={[{ required: true, message: '请选择账单日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dueDate" label="到期日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="relatedOrderType" label="关联单据类型">
                <Select placeholder="请选择单据类型" allowClear>
                  <Option value="purchase_order">采购订单</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="relatedOrderNo" label="关联单号">
                <Input placeholder="请输入关联单号" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 付款弹窗 */}
      <Modal
        title={`付款 - ${currentPayable?.billNo}`}
        open={payModalVisible}
        onOk={handlePayOk}
        onCancel={() => setPayModalVisible(false)}
        width={500}
        destroyOnClose
      >
        {currentPayable && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <Row gutter={16}>
              <Col span={12}>
                <span>应付金额：</span>
                <span style={{ fontWeight: 'bold' }}>¥{parseFloat(currentPayable.totalAmount).toFixed(2)}</span>
              </Col>
              <Col span={12}>
                <span>待付金额：</span>
                <span style={{ fontWeight: 'bold', color: '#ff4d4f' }}>¥{parseFloat(currentPayable.pendingAmount).toFixed(2)}</span>
              </Col>
            </Row>
          </div>
        )}
        <Form form={payForm} layout="vertical">
          <Form.Item
            name="amount"
            label="付款金额"
            rules={[{ required: true, message: '请输入付款金额' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0.01}
              max={currentPayable ? parseFloat(currentPayable.pendingAmount) : undefined}
              precision={2}
              prefix="¥"
              placeholder="请输入付款金额"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="paymentMethod"
                label="付款方式"
                rules={[{ required: true, message: '请选择付款方式' }]}
              >
                <Select placeholder="请选择付款方式">
                  <Option value="cash">现金</Option>
                  <Option value="bank">银行转账</Option>
                  <Option value="wechat">微信</Option>
                  <Option value="alipay">支付宝</Option>
                  <Option value="check">支票</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="paymentDate"
                label="付款日期"
                rules={[{ required: true, message: '请选择付款日期' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bankAccount" label="银行账号">
                <Input placeholder="请输入银行账号" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="transactionNo" label="交易流水号">
                <Input placeholder="请输入交易流水号" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="remark" label="备注">
            <TextArea rows={2} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title={`应付单详情 - ${payableDetail?.billNo}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={700}
      >
        {payableDetail && (
          <div>
            <Card title="基本信息" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <p><strong>应付单号：</strong>{payableDetail.billNo}</p>
                  <p><strong>供应商：</strong>{payableDetail.supplier?.name}</p>
                  <p><strong>账单日期：</strong>{dayjs(payableDetail.billDate).format('YYYY-MM-DD')}</p>
                  <p><strong>到期日期：</strong>{payableDetail.dueDate ? dayjs(payableDetail.dueDate).format('YYYY-MM-DD') : '-'}</p>
                </Col>
                <Col span={12}>
                  <p><strong>应付金额：</strong><span style={{ color: '#1890ff' }}>¥{parseFloat(payableDetail.totalAmount).toFixed(2)}</span></p>
                  <p><strong>已付金额：</strong><span style={{ color: '#52c41a' }}>¥{parseFloat(payableDetail.paidAmount).toFixed(2)}</span></p>
                  <p><strong>待付金额：</strong><span style={{ color: '#ff4d4f' }}>¥{parseFloat(payableDetail.pendingAmount).toFixed(2)}</span></p>
                  <p><strong>状态：</strong><Tag color={statusMap[payableDetail.status]?.color}>{statusMap[payableDetail.status]?.text}</Tag></p>
                </Col>
              </Row>
              {payableDetail.remark && <p><strong>备注：</strong>{payableDetail.remark}</p>}
            </Card>

            {payableDetail.payments && payableDetail.payments.length > 0 && (
              <Card title="付款记录" size="small">
                <Table
                  dataSource={payableDetail.payments}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '付款单号', dataIndex: 'paymentNo', width: 140 },
                    { title: '金额', dataIndex: 'amount', width: 100, render: (text) => `¥${parseFloat(text).toFixed(2)}` },
                    { title: '付款方式', dataIndex: 'paymentMethod', width: 100, render: (text) => paymentMethodMap[text] || text },
                    { title: '付款日期', dataIndex: 'paymentDate', width: 110, render: (text) => dayjs(text).format('YYYY-MM-DD') },
                    { title: '操作人', dataIndex: ['operator', 'realName'], width: 100 }
                  ]}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PayableList;
